import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { UseOutletContextPayroll } from './Payroll';
import PayrollAccessDenied from './PayrollAccessDenied';
import ContractHistory from './ContractHistory';
import AbsenceReport from './AbsenceReport';
import EmployeeDocuments from './EmployeeDocuments';
import { StateLanguage } from '../../../languages/config/StateLanguage';
import { Employee } from '../../../models/employee.model';
import { EmployeeDA } from '../../../services/admin/employee.service';
import { dateFormat } from '../../../scripts/date.time.format.script';
import { getCantonName } from '../../../data/cantons';
import { getCountryName } from '../../../data/country-options';
import { formatAvsNumber } from '../../../scripts/avs.validation';
import AppPreloader from '../../../components/Preloader';

export interface EmployeeViewPageProps {}

const EmployeeViewPage: React.FunctionComponent<EmployeeViewPageProps> = () => {
  const { setRoute, companyForUser } = UseOutletContextPayroll();
  const { lang } = StateLanguage();
  const { id } = useParams();

  const [employee, setEmployee] = useState<Employee | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setRoute({path: {root: lang.labels.payroll, branch: lang.labels.employees}, company: false})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (companyForUser && id) {
      loadEmployee(companyForUser.id, id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, id]);

  const loadEmployee = async (id_company: string, id_employee: string) => {
    setLoading(true);
    const response = await EmployeeDA.getEmployee(id_company, id_employee);

    if (response.status === 200) {
      setEmployee(response.data);
      setAccessDenied(false);
    } else if (response.status === 403 && response.data?.code === 'PAYROLL_ACCESS_DENIED') {
      setAccessDenied(true);
    }
    setLoading(false);
  };

  const refreshEmployee = () => {
    if (companyForUser && id) {
      loadEmployee(companyForUser.id, id);
    }
  };

  if (accessDenied) {
    return <PayrollAccessDenied />;
  }

  if (loading) {
    return <AppPreloader />;
  }

  if (!employee) {
    return (
      <div className="card">
        <div className="card-body text-center py-10">
          <p className="text-muted fw-bold fs-6">{lang.labels.noEmployeesFound}</p>
          <Link to="/main/payroll/employees/list" className="btn btn-primary mt-3">
            {lang.labels.backToList}
          </Link>
        </div>
      </div>
    );
  }

  const statusBadge = (status: string) => {
    switch (status) {
      case 'active': return 'badge-light-success';
      case 'draft': return 'badge-light-warning';
      case 'terminated': return 'badge-light-danger';
      default: return 'badge-light-primary';
    }
  };

  const statusLabel = (status: string) => {
    switch (status) {
      case 'active': return lang.labels.employeeStatusActive;
      case 'draft': return lang.labels.employeeStatusDraft;
      case 'terminated': return lang.labels.employeeStatusTerminated;
      default: return status;
    }
  };

  return (
    <>
      <div className="card mb-5">
        <div className="card-header">
          <div className="card-title">
            <h3 className="fw-bolder">
              {`${employee.contact_snapshot?.first_name ?? ''} ${employee.contact_snapshot?.last_name ?? ''}`.trim() || '-'}
            </h3>
          </div>
          <div className="card-toolbar">
            <Link to={`/main/payroll/employees/edit/${employee.id}`} className="btn btn-primary">
              {lang.labels.edit}
            </Link>
          </div>
        </div>
        <div className="card-body">
          <div className="row mb-7">
            <label className="col-lg-4 fw-bold text-muted">{lang.labels.employeeNumber}</label>
            <div className="col-lg-8 fv-row">
              <span className="fw-bold fs-6">{employee.employee_number ?? '-'}</span>
            </div>
          </div>
          <div className="row mb-7">
            <label className="col-lg-4 fw-bold text-muted">{lang.labels.status}</label>
            <div className="col-lg-8 fv-row">
              <span className={`badge ${statusBadge(employee.status)}`}>
                {statusLabel(employee.status)}
              </span>
            </div>
          </div>
          <div className="row mb-7">
            <label className="col-lg-4 fw-bold text-muted">{lang.labels.avsNumber}</label>
            <div className="col-lg-8 fv-row">
              <span className="fw-bold fs-6">{employee.avs_number ? formatAvsNumber(employee.avs_number) : '-'}</span>
            </div>
          </div>
          <div className="row mb-7">
            <label className="col-lg-4 fw-bold text-muted">{lang.labels.email}</label>
            <div className="col-lg-8 fv-row">
              <span className="fw-bold fs-6">{employee.contact_snapshot?.email ?? '-'}</span>
            </div>
          </div>
          <div className="row mb-7">
            <label className="col-lg-4 fw-bold text-muted">{lang.labels.phone}</label>
            <div className="col-lg-8 fv-row">
              <span className="fw-bold fs-6">{employee.contact_snapshot?.phone ?? '-'}</span>
            </div>
          </div>
          {employee.birth_date && (
            <div className="row mb-7">
              <label className="col-lg-4 fw-bold text-muted">{lang.labels.birthDate || 'Date of birth'}</label>
              <div className="col-lg-8 fv-row">
                <span className="fw-bold fs-6">{dateFormat(lang.code, employee.birth_date)}</span>
              </div>
            </div>
          )}
          {employee.nationality && (
            <div className="row mb-7">
              <label className="col-lg-4 fw-bold text-muted">{lang.labels.nationality || 'Nationality'}</label>
              <div className="col-lg-8 fv-row">
                <span className="fw-bold fs-6">{getCountryName(employee.nationality, lang.code)}</span>
              </div>
            </div>
          )}
          {employee.work_canton && (
            <div className="row mb-7">
              <label className="col-lg-4 fw-bold text-muted">{lang.labels.workCanton}</label>
              <div className="col-lg-8 fv-row">
                <span className="fw-bold fs-6">{`${employee.work_canton} — ${getCantonName(employee.work_canton, lang.code)}`}</span>
              </div>
            </div>
          )}
          <div className="row mb-7">
            <label className="col-lg-4 fw-bold text-muted">{lang.labels.expenseAccess}</label>
            <div className="col-lg-8 fv-row">
              <span className="fw-bold fs-6">
                {employee.expense_access === 'forced_on'
                  ? lang.labels.expenseAccessForcedOn
                  : employee.expense_access === 'forced_off'
                    ? lang.labels.expenseAccessForcedOff
                    : lang.labels.expenseAccessAuto}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Contract History */}
      {companyForUser && id && (
        <div className="card">
          <div className="card-body">
            <ContractHistory
              companyId={companyForUser.id}
              employeeId={id}
              lang={lang}
              onContractChange={refreshEmployee}
            />
          </div>
        </div>
      )}

      {/* Absence Report */}
      {companyForUser && id && (
        <AbsenceReport
          companyId={companyForUser.id}
          employeeId={id}
          lang={lang}
          editable
        />
      )}

      {/* Documents */}
      {companyForUser && id && employee && (
        <EmployeeDocuments
          companyId={companyForUser.id}
          employeeId={id}
          vouchers={employee.vouchers ?? []}
          lang={lang}
          onChanged={refreshEmployee}
        />
      )}
    </>
  );
};

export default EmployeeViewPage;
